<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Usuários</h1>
      <button
        @click="showCreateModal = true"
        class="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
      >
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        Novo Usuário
      </button>
    </div>
    
    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
      <p class="mt-4 text-gray-600">Carregando usuários...</p>
    </div>
    
    <!-- Empty -->
    <div v-else-if="users.length === 0" class="bg-white rounded-xl shadow-sm p-8 text-center">
      <p class="text-gray-500">Nenhum usuário cadastrado</p>
    </div>
    
    <!-- Table -->
    <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Perfil</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="user in users" :key="user.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">{{ user.full_name }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-500">{{ user.email }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="{
                  'bg-purple-100 text-purple-800': user.role === 'super_admin',
                  'bg-blue-100 text-blue-800': user.role === 'company_admin',
                  'bg-gray-100 text-gray-800': user.role === 'company_staff'
                }"
              >
                {{ formatRole(user.role) }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-500">{{ getCompanyName(user.company_id ?? null) }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
              >
                {{ user.is_active ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button
                @click="editUser(user)"
                class="text-orange-600 hover:text-orange-900 mr-3"
              >
                Editar
              </button>
              <button
                @click="deleteUser(user)"
                class="text-red-600 hover:text-red-900"
              >
                Excluir
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Modal -->
    <UserFormModal
      v-if="showCreateModal || showEditModal"
      :user="selectedUser"
      :is-edit="showEditModal"
      @close="closeModal"
      @save="handleSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { userApi, companyApi } from '@/utils/api'
import { notifyError, notifySuccess } from '@/utils/notifications'
import UserFormModal from './UserFormModal.vue'
import { confirmDialog } from '@/composables/useConfirm'
import type { User } from '@/types'

const users = ref<User[]>([])
const companies = ref<any[]>([])
const loading = ref(true)
const showCreateModal = ref(false)
const showEditModal = ref(false)
const selectedUser = ref<User | null>(null)

const loadUsers = async () => {
  loading.value = true
  try {
    const [usersRes, companiesRes] = await Promise.all([
      userApi.getAll(),
      companyApi.getAll()
    ])
    users.value = usersRes.data
    companies.value = companiesRes.data
  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
    notifyError('Erro ao carregar usuários')
  } finally {
    loading.value = false
  }
}

const getCompanyName = (companyId: number | null | undefined) => {
  if (!companyId) return '-'
  const company = companies.value.find(c => c.id === companyId)
  return company ? company.name : '-'
}

const formatRole = (role: string) => {
  const roles: Record<string, string> = {
    'super_admin': 'Super Admin',
    'company_admin': 'Admin Empresa',
    'company_staff': 'Funcionário'
  }
  return roles[role] || role
}

const editUser = (user: User) => {
  selectedUser.value = user
  showEditModal.value = true
}

const deleteUser = async (user: User) => {
  if (!await confirmDialog({ title: 'Confirmar exclusão', message: `Tem certeza que deseja excluir o usuário "${user.full_name}"?`, danger: true, confirmLabel: 'Excluir' })) return
  
  try {
    await userApi.delete(user.id)
    notifySuccess('Usuário excluído com sucesso!')
    loadUsers()
  } catch (error: any) {
    notifyError('Erro: ' + (error.response?.data?.detail || 'Erro ao excluir usuário'))
  }
}

const closeModal = () => {
  showCreateModal.value = false
  showEditModal.value = false
  selectedUser.value = null
}

const handleSave = () => {
  closeModal()
  loadUsers()
}

onMounted(() => {
  loadUsers()
})
</script>
